import { gql } from '@apollo/client'
import getApolloClient from 'src/apollo-client'

/**
 * Server-side mirror of browser-fired Meta `Lead` pixel events via Meta
 * Conversions API (CAPI). Recovers the ~30-40% signal lost to iOS ATT
 * (App Tracking Transparency) opt-outs and ad-blocker traffic that the
 * browser pixel never reaches Meta.
 *
 * Dedup contract: Meta deduplicates browser-pixel + CAPI fires that share
 * the SAME `event_id`. Every caller MUST pass the exact `event_id` it
 * already passed to `fbp.event('Lead', { event_id })` — these two fires
 * are a single conceptual event in two transports.
 *
 * Scope (intentionally narrow): only the four high-intent browser `Lead`
 * surfaces get a CAPI mirror — `generateLead`, `completeKyc`, `purchase`,
 * `onDemandLead`. The engagement-level `Lead` fired from `addToCart`
 * (Patch B 2026-05-25) is browser-only because volume × Meta CAPI cost
 * is unjustified for retargeting-audience builds where browser-only
 * signal is already sufficient.
 *
 * Server-side: routes through `recordBrowserLeadConversion` GraphQL
 * mutation → `sendConversionEvent(ConversionEventType.BrowserLead)` →
 * Meta CAPI only (Google/Microsoft per-vertical actions own those
 * platforms; firing generic `Lead` there would double-count).
 *
 * Failure mode: fire-and-forget. Network errors / GraphQL errors are
 * silently dropped — losing one CAPI mirror is not a user-facing
 * concern, and the browser pixel already covered the non-ATT path.
 */

const RECORD_BROWSER_LEAD = gql`
  mutation RecordBrowserLeadConversion(
    $input: RecordBrowserLeadConversionInput!
  ) {
    recordBrowserLeadConversion(input: $input)
  }
`

export type BrowserLeadCapiInput = {
  /**
   * MUST match the `event_id` passed to `fbp.event('Lead', { event_id })`
   * for Meta-side dedup. Generated via `getEventId()` in spotmeEvents.ts.
   */
  eventId: string
  /** Funnel category — typically `enrichment.deal_type ?? 'monthly'`. */
  contentCategory?: string
  /** Surface descriptor — typically the event name (`complete_kyc`, etc). */
  contentName?: string
  /** Storage / parking / other — for audience segmentation. */
  vertical?: string
  /** MXN total deal value when applicable. */
  value?: number
  currency?: string
  /** Listing context for catalog matching when present. */
  configurationId?: string
  locationId?: string
}

let cachedClient: ReturnType<typeof getApolloClient> | undefined
const getClient = () => cachedClient ?? (cachedClient = getApolloClient())

export const recordBrowserLeadCapi = (input: BrowserLeadCapiInput): void => {
  // Client-side only — server-render passes (e.g. getStaticProps) should
  // never trigger this. Apollo client would happily run there but the
  // CAPI fire would be attributed to a bot user-agent.
  if (typeof window === 'undefined') return

  getClient()
    .mutate({ mutation: RECORD_BROWSER_LEAD, variables: { input } })
    .catch(() => {
      // Fire-and-forget. The browser pixel already covered the non-ATT
      // path; CAPI is bonus coverage that can fail silently.
    })
}
